import CTNFilterBar, { CTNFilterButton, CTNFilterDropdown, CTNFilterSearch } from '@ctn/ui/components/ctn-filter-bar';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import CTNTable from '@ctn/ui/components/ctn-table';
import { type CTNFilterBarRef } from '@ctn/ui/types/ctn-filter-bar';
import { type CTNTableRef } from '@ctn/ui/types/ctn-table';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import {
	type ISolicitudListadoPayload,
	type ISolicitudResponse,
	postSolicitudEliminacion,
	postSolicitudListado,
} from '@/services/solicitudes.service';

import { PAGE_SIZE } from '@/constants/table.constants';
import { notificationsConfig } from '@/helpers/notifications';
import { type ISolicitud } from '@/interfaces/model/solicitudes.interface';

import getColumns from './columns';
import { defaultValues, type FilterFormData, getSchema, PAYLOAD_PAGINATION, payloadDefault } from './config';

type IDraft = {
	readonly goToDetail: (rowData: ISolicitud) => void;
	readonly idTipoTramite: number;
};

const BorradoresListado = ({ goToDetail, idTipoTramite }: IDraft): ReactElement => {
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const filterBarRef = useRef<CTNFilterBarRef>(null);
	const location = useLocation();
	const {
		maestrosGeneric: { tipoTramite: tipoTramiteMaestro },
	} = useGlobalContext();
	const { navigateData, setNavigateData } = usePageContext();

	const tableRef = useRef<CTNTableRef<ISolicitud>>(null);
	const { loading, triggerFetch } = useApiData({ loading: ['listado', 'eliminar'] as const });
	const [dataTable, setDataTable] = useState<ISolicitudResponse>({ totalResults: 10, listado: [] });
	const [pagination, setPagination] = useState<Pick<ISolicitudListadoPayload, 'limit' | 'start'>>(PAYLOAD_PAGINATION);

	const { watch, control, reset, setValue } = useForm<FilterFormData>({
		resolver: getSchema(),
		mode: 'onChange',
		defaultValues: defaultValues(idTipoTramite),
	});

	const search = watch('search');
	const tipoBusqueda = search?.[0]?.key ?? defaultValues(idTipoTramite).tipoBusqueda;
	const tipoTramite = watch('tipoTramite');
	const [isReady, setIsReady] = useState(false);
	const isInitialLoad = useRef(true);

	useEffect(() => {
		if (!navigateData?.filter?.urls?.some(path => location.pathname.includes(path))) {
			const { filter, ...resto } = navigateData ?? {};
			setNavigateData(resto);
			getData({ ...PAYLOAD_PAGINATION, [tipoBusqueda]: search?.[0]?.search ?? '', listaIdsTipoTramite: tipoTramite.map(Number) });
			setIsReady(true);
			return;
		}

		const {data} = navigateData.filter;
		if (!data) {
			getData({ ...PAYLOAD_PAGINATION, [tipoBusqueda]: search?.[0]?.search ?? '', listaIdsTipoTramite: tipoTramite.map(Number) });
			setIsReady(true);
			return;
		}
		reset({
			...defaultValues(idTipoTramite),
			search: [{ key: data.tipoBusqueda ?? defaultValues(idTipoTramite).tipoBusqueda, search: data.numeroTramite }],
		});
		filterBarRef.current?.addFilters({
			search: data.numeroTramite ? [{ key: data.tipoBusqueda ?? defaultValues(idTipoTramite).tipoBusqueda, search: data.numeroTramite }] : [],
		});
		const { filter, ...resto } = navigateData ?? {};
		setNavigateData(resto);
		setIsReady(true);
		isInitialLoad.current = false;
	}, []);

	const getData = async (
		payload: Pick<ISolicitudListadoPayload, 'limit' | 'start'> & Partial<ISolicitudListadoPayload>,
	) => {
		const { data } = await triggerFetch({
			fetch: postSolicitudListado,
			payload: { ...payloadDefault(idTipoTramite), ...payload },
			name: 'listado',
		});
		if (data) {
			setDataTable(data);
		}
	};

	const onPageChange = (start: number) => {
		let newPagination = {
			start: PAGE_SIZE.OPERACIONES * (start - 1),
			limit: PAGE_SIZE.OPERACIONES,
		};

		if (start === 1) {
			newPagination = {
				start: 0,
				limit: PAGE_SIZE.OPERACIONES,
			};
		}
		getData({ ...newPagination, [tipoBusqueda]: search?.[0]?.search ?? '', listaIdsTipoTramite: tipoTramite.map(Number) });
		setPagination(newPagination);
	};

	const onEliminar = async (data: ISolicitud) => {
		const { emisor, tipoTramite, id } = data;
		const payload = {
			nifEmisor: emisor.nif,
			idTipoSector: emisor.tipoSector.id,
			idTipoTramite: tipoTramite.id,
			id,
		};

		const { code } = await triggerFetch({
			fetch: postSolicitudEliminacion,
			payload,
			name: 'eliminar',
			error: 'Ha ocurrido un error al borrar el registro.',
		});

		if (code === 200) {
			onPageChange(1);
		}
	};

	const eliminar = (data: ISolicitud) => {
		openNotification(
			notificationTemplate.removeItem({
				title: 'Eliminar borrador',
				content: 'La eliminación del borrador será irreversible. ¿Estás seguro que deseas continuar?',
				onAccept() {
					onEliminar(data);
				},
			}),
		);
	};

	useEffect(() => {
		if (!isReady) return;
		if (isInitialLoad.current) {
			isInitialLoad.current = false;
			return;
		}
		getData({ ...pagination, [tipoBusqueda]: search?.[0]?.search ?? '', listaIdsTipoTramite: tipoTramite.map(Number) });
		setNavigateData({ filter: { urls: [location.pathname], data: {numeroTramite: search?.[0]?.search ?? ''} } });
	}, [search, tipoBusqueda, ...tipoTramite, isReady]);

	return (
		<section className="u-mt-24">
			<CTNFilterBar
				ref={filterBarRef}
				tagBar
				show
				className="c-pnp-filter-bar"
				control={control as any}
				setValue={setValue as any}
				reset={reset as any}
			>
				<CTNFilterSearch
					name="search" placeholder="Nº solicitud" label="Buscar"
					tag="Búsqueda"
					options={[{ label: 'Nº solicitud', value: 'numeroTramiteExterno' }]} />
				<CTNFilterDropdown
					disabled
					keepDefaultValue
					multiple
					showArrow={false}
					label="Tipo de negocio jurídico"
					valueFormatter={() => 'Tipo de negocio jurídico'}
					placeholder="Tipo de negocio jurídico"
					name="tipoTramite"
					tag="Tipo de negocio jurídico"
					options={tipoTramiteMaestro?.map(({ descripcion, id }) => ({
						label: descripcion,
						value: String(id),
					}))}
				/>
				<CTNFilterButton
					name="clearAll"
					label="Reiniciar filtros"
					tag="Limpiar"
					icon={[{ icon: 'restartCircle', position: 'left', color: 'blue-60' }]}
					onClick={() => filterBarRef.current?.resetFilter()}
				/>
			</CTNFilterBar>
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 ]">
				<div className="c-pnp-box__body">
					<CTNTable
						ref={tableRef}
						manualPagination
						idData="id"
						loading={loading.listado}
						loadingConf={{ spinner: true }}
						columns={getColumns({ tipoTramite: tipoTramiteMaestro, eliminar, goToDetail })}
						data={dataTable.listado}
						totalData={dataTable.totalResults}
						manualPageCount={dataTable.totalResults / PAGE_SIZE.OPERACIONES}
						pageSize={PAGE_SIZE.OPERACIONES}
						className="c-pnp-table"
						onPageChange={onPageChange}
					/>
				</div>
			</div>
		</section>
	);
};

export default BorradoresListado;
